import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Dependency,
  CreateDependencyRequest,
  UpdateDependencyRequest
} from '../models/dependency.interface';
import { environment } from '../../../../environments/environment';
import { ApiEnvelope } from '../../../shared/models/api-envelope';
import { DependencyMapper } from '../models/dependency.mapper';
import { DependencyDto } from '../models/dependency.dto';

@Injectable({
  providedIn: 'root'
})
export class DependencyService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.baseUrl.generic}/Dependencia`;

  // Envelope común de la API
  private unwrapResponse<T>() {
    return map((response: T | ApiEnvelope<T>) => {
      const maybe = response as ApiEnvelope<T>;
      return maybe && (maybe as { data?: T }).data !== undefined
        ? (maybe.data as T)
        : (response as T);
    });
  }

  // Normaliza claves del backend -> modelo de UI
  private normalizeDependency(raw: DependencyDto): Dependency {
    return DependencyMapper.fromDto(raw);
  }

  private normalizeList(list: DependencyDto[] | Dependency[]): Dependency[] {
    return DependencyMapper.fromDtoList(list as DependencyDto[]);
  }

  /**
   * Obtiene todas las dependencias
   */
  getDependencies(): Observable<Dependency[]> {
    return this.http
      .get<Dependency[] | ApiEnvelope<DependencyDto[]>>(`${this.baseUrl}/GetAll`)
      .pipe(
        this.unwrapResponse<Dependency[]>(),
        map((res: Dependency[] | DependencyDto[]) => this.normalizeList(res as DependencyDto[]))
      );
  }

  /**
   * Obtiene una dependencia por ID
   */
  getDependencyById(id: number): Observable<Dependency> {
    const params = new HttpParams().set('id', id.toString());
    return this.http
      .get<Dependency | ApiEnvelope<DependencyDto>>(`${this.baseUrl}/GetByID`, { params })
      .pipe(
        this.unwrapResponse<Dependency>(),
        map((res: Dependency | DependencyDto) =>
          this.normalizeDependency((res ?? {}) as DependencyDto)
        )
      );
  }

  /**
   * Crea una nueva dependencia
   */
  createDependency(dependency: CreateDependencyRequest): Observable<Dependency> {
    return this.http
      .post<Dependency | ApiEnvelope<DependencyDto>>(`${this.baseUrl}/Create`, dependency)
      .pipe(
        this.unwrapResponse<Dependency>(),
        map((res: Dependency | DependencyDto) =>
          this.normalizeDependency((res ?? {}) as DependencyDto)
        )
      );
  }

  /**
   * Actualiza una dependencia existente
   */
  updateDependency(id: number, dependency: UpdateDependencyRequest): Observable<boolean> {
    return this.http
      .put<boolean | ApiEnvelope<boolean>>(`${this.baseUrl}/${id}`, dependency)
      .pipe(this.unwrapResponse<boolean>());
  }

  /**
   * Activa o desactiva una dependencia
   */
  toggleDependencyStatus(id: number, isActive: boolean): Observable<boolean> {
    return this.http
      .patch<boolean | ApiEnvelope<boolean>>(`${this.baseUrl}/${id}/${isActive}`, {})
      .pipe(this.unwrapResponse<boolean>());
  }

  /**
   * Obtiene dependencias padre (nodo raíz)
   */
  getParentDependencies(): Observable<Dependency[]> {
    return this.http
      .get<Dependency[] | ApiEnvelope<DependencyDto[]>>(`${this.baseUrl}/DependenciasPadres`)
      .pipe(
        this.unwrapResponse<Dependency[]>(),
        map((res: Dependency[] | DependencyDto[]) => this.normalizeList(res as DependencyDto[]))
      );
  }

  /**
   * Busca dependencias por criterios
   */
  searchDependencies(query: string): Observable<Dependency[]> {
    const params = new HttpParams().set('q', query);
    return this.http
      .get<Dependency[] | ApiEnvelope<DependencyDto[]>>(`${this.baseUrl}/Search`, { params })
      .pipe(
        this.unwrapResponse<Dependency[]>(),
        map((res: Dependency[] | DependencyDto[]) => this.normalizeList(res as DependencyDto[]))
      );
  }

  /**
   * Verifica si un código ya existe
   */
  checkCodeExists(code: string, excludeId?: number): Observable<boolean> {
    const params = excludeId
      ? new HttpParams().set('code', code).set('excludeId', excludeId.toString())
      : new HttpParams().set('code', code);
    return this.http
      .get<boolean | ApiEnvelope<boolean>>(`${this.baseUrl}/CheckCode`, { params })
      .pipe(this.unwrapResponse<boolean>());
  }

  /**
   * Elimina una dependencia
   */
  deleteDependency(id: number): Observable<boolean> {
    return this.http
      .delete<boolean | ApiEnvelope<boolean>>(`${this.baseUrl}/Delete/${id}`)
      .pipe(this.unwrapResponse<boolean>());
  }
}
